import { z } from "zod";
import { Uid } from "./docs.js";
import { AccountId } from "./ids.js";
/**
 * Sign-up by invitation (architecture §5). `invites/{email}` is written by `admin.invite` and names
 * one shared (never Personal) workspace the invitee joins on first sign-in (WP-028); the
 * invitation email goes out from the inviting admin's own mailbox through `admin.sendInvitation`,
 * a `send` action (§9): at once from the web with presence, an approval from the CLI or MCP.
 */
export const InviteDoc = z.object({
    email: z.string().email(),
    name: z.string(),
    invitedBy: Uid,
    /** The shared workspace the invitee joins; null on invites written before WP-028. */
    workspaceId: z.string().nullable().default(null),
    createdAt: z.date(),
    /** When the invitation email last went out, and from which address; null until then. */
    emailedAt: z.date().nullable().default(null),
    emailedFrom: z.string().email().nullable().default(null),
    acceptedAt: z.date().nullable().default(null),
    uid: Uid.nullable().default(null),
});
export const InviteName = z.string().min(1).max(80);
export const AdminInviteInput = z.object({
    email: z.string().email(),
    name: InviteName,
    /** A shared workspace the caller owns; Personal is refused (`personal_is_private`). */
    workspaceId: z.string().min(1),
    /** One of the caller's own accounts to email the invitation from; absent: no email. */
    sendFromAccountId: AccountId.optional(),
});
/** The `admin.sendInvitation` action's input, and `admin.resendInvitation`'s. */
export const InvitationEmailInput = z.object({ email: z.string().email(), sendFromAccountId: AccountId });
/** What happened to the invitation email; a failure leaves the invite in place. */
export const InvitationEmailOutcome = z.discriminatedUnion("status", [
    z.object({ status: z.literal("not_requested") }),
    z.object({ status: z.literal("sent"), from: z.string().email() }),
    z.object({ status: z.literal("awaiting_approval"), approvalId: z.string() }),
    z.object({ status: z.literal("failed"), reason: z.string() }),
]);
export const AdminInviteOutput = z.object({
    email: z.string().email(),
    workspaceId: z.string(),
    workspaceName: z.string(),
    /** True when they had already signed in and were added to the workspace at once. */
    added: z.boolean(),
    emailOutcome: InvitationEmailOutcome,
});
export const InvitationView = z.object({
    email: z.string().email(),
    name: z.string(),
    /** Null for invites written before WP-028 ("no workspace"). */
    workspaceId: z.string().nullable(),
    workspaceName: z.string().nullable(),
    createdAt: z.date(),
    emailedAt: z.date().nullable(),
    emailedFrom: z.string().email().nullable(),
    acceptedAt: z.date().nullable(),
});
export const AdminInvitationsOutput = z.array(InvitationView);
/** A workspace's invitations that have not been accepted yet, for its own page. */
export const WorkspaceInvitationView = z.object({ email: z.string().email(), name: z.string(), createdAt: z.date() });
export const WorkspaceInvitationsOutput = z.array(WorkspaceInvitationView);
//# sourceMappingURL=invites.js.map